import React, { useState } from "react";

import { useDismissLabelDemandMutation, useFetchLabelDemandQuery } from "../../../api/printingApi";
import { useLanguage } from "../../../contexts/LanguageContext";
import { Btn, EmptyState, Spinner, Stepper, Thumb, cx } from "../../catalogProductStudio/edit/StudioUi";
import { getGroupSelectionState } from "../state/printSelectionModel";
import { ItemCounts, ItemIdentity, TriStateCheckbox } from "../PrintCenterUi";
import { fill, formatDateTime, formatDay, getApiErrorDetails, plural } from "../printCenterModel";

const DISMISS_REASONS = ["dismissReasonLabelled", "dismissReasonSupplier", "dismissReasonMiscount", "dismissReasonOther"];

function DismissDialog({ tt, entry, onClose, notify }) {
  // Defaults to this day's labels; up to everything the item still needs.
  const [quantity, setQuantity] = useState(String(Math.min(entry.outstanding || entry.item.demand.outstanding, entry.item.demand.outstanding)));
  const [reasonKey, setReasonKey] = useState(DISMISS_REASONS[0]);
  const [note, setNote] = useState("");
  const [dismiss, { isLoading }] = useDismissLabelDemandMutation();
  const max = entry.item.demand.outstanding;
  const reason = reasonKey === "dismissReasonOther" ? note.trim() : tt(reasonKey);
  const valid = Number(quantity) >= 1 && Number(quantity) <= max && reason;

  const submit = async () => {
    try {
      await dismiss({ key: entry.item.key, quantity: Number(quantity), reason }).unwrap();
      notify(plural(tt, "dismissDone", Number(quantity)), "success");
      onClose();
    } catch (error) {
      const { code, details } = getApiErrorDetails(error);
      notify(
        code === "LABEL_DEMAND_CHANGED" ? fill(tt("dismissChanged"), { count: details?.available ?? 0 }) : tt("actionError"),
        "danger"
      );
    }
  };

  return (
    <div className="pse-overlay pse-overlay--dialog" role="presentation">
      <button type="button" className="pse-overlay__backdrop" aria-label={tt("close")} onClick={onClose} />
      <div className="pse-dialog pc-dialog" role="dialog" aria-modal="true" aria-labelledby="pc-dismiss-title">
        <h2 id="pc-dismiss-title" className="pse-dialog__title">{tt("dismissTitle")}</h2>
        <ItemIdentity tt={tt} item={entry.item} compact />
        <p className="pc-muted pc-small">{tt("dismissCopy")}</p>
        <div className="pc-form-row">
          <span className="pc-filter__label">{tt("dismissQuantity")}</span>
          <Stepper value={quantity} onChange={setQuantity} min={1} max={max} ariaLabel={tt("dismissQuantity")} decreaseLabel={tt("decrease")} increaseLabel={tt("increase")} />
          <span className="pc-muted pc-small">{fill(tt("dismissMax"), { count: max })}</span>
        </div>
        <fieldset className="pc-reasons">
          <legend className="pc-filter__label">{tt("dismissReason")}</legend>
          {DISMISS_REASONS.map((key) => (
            <label key={key} className="pc-radio">
              <input type="radio" name="pc-dismiss-reason" checked={reasonKey === key} onChange={() => setReasonKey(key)} />
              <span>{tt(key)}</span>
            </label>
          ))}
          {reasonKey === "dismissReasonOther" ? (
            <input
              className="pse-input"
              maxLength={200}
              value={note}
              placeholder={tt("dismissReasonPlaceholder")}
              onChange={(event) => setNote(event.target.value)}
              data-autofocus
            />
          ) : null}
        </fieldset>
        <div className="pse-dialog__actions">
          <Btn onClick={onClose}>{tt("cancel")}</Btn>
          <Btn variant="danger" disabled={!valid || isLoading} onClick={submit}>
            {tt("dismissConfirm")}
          </Btn>
        </div>
      </div>
    </div>
  );
}

/**
 * Selection by receipt: each entry knows which receipts its labels come from,
 * so selecting a day, a product, a receipt or one variant adds exactly those
 * labels as new stock (merged like a "Print X labels" link, never twice).
 */
const receiptSelection = (entryList) => {
  const receipts = new Map();
  const items = new Map();
  entryList.forEach((entry) => {
    if (entry.item.demand.outstanding <= 0) return;
    items.set(entry.item.key, entry.item);
    entry.receipts.forEach((receipt) => {
      const current = receipts.get(receipt.receiptId) || { id: receipt.receiptId, kind: receipt.kind, needsLabels: true, lines: [] };
      current.lines.push({ key: entry.item.key, outstanding: receipt.quantity });
      receipts.set(receipt.receiptId, current);
    });
  });
  return { receipts: [...receipts.values()], items: [...items.values()] };
};

function EntryDetails({ tt, entry, language, onOpenJob }) {
  return (
    <div className="pc-needs-entry__details">
      <ul className="pc-needs-receipts">
        {entry.receipts.map((receipt) => (
          <li key={receipt.receiptId}>
            <span>
              {fill(tt(`receivedVia_${receipt.kind}`), {
                time: formatDateTime(receipt.receivedAt, language),
                name: receipt.receivedBy || "—",
              })}
            </span>
            <span className="pc-muted">
              {fill(tt("receiptLineCounts"), { received: receipt.required ?? receipt.quantity, open: receipt.quantity })}
            </span>
          </li>
        ))}
      </ul>
      {(entry.pendingJobs || []).map((job) => (
        <p key={job.jobId} className="pc-needs-entry__held">
          {fill(tt(job.quantity === 1 ? "heldInJobOne" : "heldInJob"), {
            count: job.quantity,
            name: job.createdByName || "—",
            job: job.jobId,
          })}{" "}
          <button type="button" className="pc-link" onClick={() => onOpenJob(job.jobId)}>
            {fill(tt("openJobNumber"), { job: job.jobId })}
          </button>
        </p>
      ))}
      {entry.dismissed ? (
        <details className="pc-needs-entry__dismissed">
          <summary>{plural(tt, "dismissedCount", entry.dismissed)}</summary>
          <ul>
            {(entry.dismissals || []).map((dismissal, index) => (
              <li key={`${dismissal.createdAt}-${index}`}>
                {fill(tt("dismissalLine"), {
                  count: dismissal.quantity,
                  reason: dismissal.reason,
                  name: dismissal.actorName || "—",
                  time: formatDateTime(dismissal.createdAt, language),
                })}
              </li>
            ))}
          </ul>
        </details>
      ) : null}
    </div>
  );
}

function NeedsLabelsView({ tt, entries, onSelect, onSelectReceipts, onDeselect, onOpenJob, notify, onBrowseCatalog }) {
  const { language } = useLanguage();
  const { data, isLoading, isError, refetch, isFetching } = useFetchLabelDemandQuery({});
  const [dismissing, setDismissing] = useState(null);

  if (isLoading) {
    return (
      <div className="pc-loading">
        <Spinner label={tt("loading")} />
      </div>
    );
  }
  if (isError || !data) {
    return <EmptyState icon="alert" title={tt("loadError")} action={<Btn onClick={refetch}>{tt("retry")}</Btn>} />;
  }

  const allItems = data.groups.flatMap((group) => group.products.flatMap((product) => product.entries.map((entry) => entry.item)));
  const uniqueItems = [...new Map(allItems.map((item) => [item.key, item])).values()].filter((item) => item.demand.outstanding > 0);
  const selectNeeds = (items) => onSelect(items.filter((item) => item.demand.outstanding > 0), "needs_labels");
  const openEntries = (list) => list.filter((entry) => entry.item.demand.outstanding > 0);
  const groupState = (list) => getGroupSelectionState(openEntries(list).map((entry) => entry.item), entries);
  const toggleEntries = (list) => {
    const relevant = openEntries(list);
    if (groupState(relevant) === "all") onDeselect(relevant.map((entry) => entry.item.key));
    else onSelectReceipts(receiptSelection(relevant));
  };

  return (
    <div className={cx("pc-needs", isFetching && "is-refreshing")}>
      {!data.groups.length ? (
        <EmptyState
          icon="checkCircle"
          title={tt("needsEmptyTitle")}
          action={<Btn icon="search" onClick={onBrowseCatalog}>{tt("browseCatalog")}</Btn>}
        >
          {tt("needsEmptyCopy")}
        </EmptyState>
      ) : (
        <>
          <section className="pc-needs__hero">
            <div>
              <p className="pc-eyebrow-label">{tt("needsHeroEyebrow")}</p>
              <h2 className="pc-needs__total">
                {data.summary.outstanding > 0 ? plural(tt, "labelsNeeded", data.summary.outstanding) : tt("needsAllPendingTitle")}
              </h2>
              <p className="pc-muted">
                {fill(tt("needsHeroMeta"), { items: data.summary.items, products: data.summary.products })}
                {data.summary.pending ? ` · ${plural(tt, "pendingElsewhere", data.summary.pending)}` : ""}
              </p>
            </div>
            {data.summary.outstanding > 0 ? (
              <Btn
                variant="primary"
                icon="check"
                disabled={!uniqueItems.some((item) => !entries[item.key])}
                onClick={() => selectNeeds(uniqueItems.filter((item) => !entries[item.key]))}
              >
                {plural(tt, "selectAllNeeded", data.summary.outstanding)}
              </Btn>
            ) : null}
          </section>

          {data.groups.map((group) => {
            const dayEntries = group.products.flatMap((product) => product.entries);
            const dayLabel = formatDay(group.day, language);
            return (
              <section key={group.day} className="pc-day" aria-label={dayLabel}>
                <header className="pc-day__head">
                  <TriStateCheckbox
                    state={groupState(dayEntries)}
                    onChange={() => toggleEntries(dayEntries)}
                    label={fill(tt("selectDay"), { day: dayLabel })}
                  />
                  <h3 className="pc-day__title">{dayLabel}</h3>
                  <span className="pc-muted pc-small">{plural(tt, "labelCount", group.outstanding)}</span>
                </header>

                {group.products.map((product) => {
                  const productReceipts = [
                    ...new Map(
                      product.entries.flatMap((entry) => entry.receipts).map((receipt) => [receipt.receiptId, receipt])
                    ).values(),
                  ];
                  return (
                    <article key={product.product.id} className="pc-needs-product">
                      <header className="pc-needs-product__head">
                        <TriStateCheckbox
                          state={groupState(product.entries)}
                          onChange={() => toggleEntries(product.entries)}
                          label={fill(tt("selectProduct"), { name: product.product.title })}
                        />
                        <Thumb src={product.product.imageUrl} size={44} fallbackLabel={product.product.title} />
                        <div className="pc-needs-product__titles">
                          <strong>{product.product.title}</strong>
                          <span className="pc-muted pc-small">
                            {[product.product.baseSku, product.product.brandName].filter(Boolean).join(" · ")}
                          </span>
                        </div>
                        <span className="pc-needs-product__count">{plural(tt, "labelCount", product.outstanding)}</span>
                      </header>
                      {productReceipts.length > 1 ? (
                        <div className="pc-needs-product__receipts" role="group" aria-label={tt("selectByReceipt")}>
                          {productReceipts.map((receipt) => {
                            const receiptEntries = product.entries
                              .map((entry) => ({
                                ...entry,
                                receipts: entry.receipts.filter((line) => line.receiptId === receipt.receiptId),
                              }))
                              .filter((entry) => entry.receipts.length);
                            const count = receiptEntries.reduce((sum, entry) => sum + entry.receipts[0].quantity, 0);
                            return (
                              <button
                                key={receipt.receiptId}
                                type="button"
                                className="pc-chip-btn"
                                disabled={!count}
                                onClick={() => onSelectReceipts(receiptSelection(receiptEntries))}
                              >
                                {fill(tt("selectReceipt"), {
                                  receipt: fill(tt(`receiptName_${receipt.kind}`), { id: receipt.receiptId }),
                                  count,
                                })}
                              </button>
                            );
                          })}
                        </div>
                      ) : null}
                      <ul className="pc-needs-entries">
                        {product.entries.map((entry) => {
                          const selected = entries[entry.item.key];
                          const disabled = entry.item.demand.outstanding <= 0;
                          return (
                            <li key={entry.item.key} className={cx("pc-needs-entry", selected && "is-selected")}>
                              <input
                                type="checkbox"
                                className="pc-check"
                                checked={Boolean(selected)}
                                disabled={disabled}
                                aria-label={fill(tt("selectItem"), { name: `${product.product.title} ${entry.item.variantTitle || ""}`.trim() })}
                                onChange={() =>
                                  selected ? onDeselect([entry.item.key]) : onSelectReceipts(receiptSelection([entry]))
                                }
                              />
                              <ItemIdentity tt={tt} item={entry.item} compact showProduct={false} />
                              <div className="pc-needs-entry__numbers">
                                <span className="pc-needs-entry__need">
                                  {entry.outstanding === entry.item.demand.outstanding
                                    ? plural(tt, "needsLabelsCount", entry.outstanding)
                                    : fill(tt("needsOfTotal"), { count: entry.outstanding, total: entry.item.demand.outstanding })}
                                </span>
                                <span className="pc-muted pc-small">
                                  {fill(tt("entryReceivedCount"), { count: entry.required ?? entry.outstanding })}
                                </span>
                                <ItemCounts tt={tt} item={entry.item} />
                              </div>
                              <button
                                type="button"
                                className="pc-link pc-needs-entry__dismiss"
                                disabled={disabled}
                                onClick={() => setDismissing(entry)}
                              >
                                {tt("dismiss")}
                              </button>
                              <EntryDetails tt={tt} entry={entry} language={language} onOpenJob={onOpenJob} />
                            </li>
                          );
                        })}
                      </ul>
                    </article>
                  );
                })}
              </section>
            );
          })}
        </>
      )}

      {dismissing ? <DismissDialog tt={tt} entry={dismissing} onClose={() => setDismissing(null)} notify={notify} /> : null}
    </div>
  );
}

export default NeedsLabelsView;
